import { useMemo } from "react"
import { toast } from "sonner"
import { ShotModelMenu } from "@/components/shot-panel/shot-model-menu"
import { useShotPanelWorkspace } from "@/hooks/use-shot-panel-workspace"
import { useTranslation } from "@/hooks/use-translation"
import { sortedCatalogModelOptions } from "@/lib/generation-model-option"
import { patchShotVideoModelKey } from "@/lib/inertia-actions"
import {
  effectiveShotVideoModelKey,
  findShotVideoModel,
  videoResolutionAfterModelChange,
} from "@/lib/shot-video-settings"

export type ShotVideoModelSaved = {
  videoModelKey: string | null
  videoResolution: string | null
}

export type ShotVideoModelChipProps = {
  projectId: number
  shotId: number
  /** Shot override; null/undefined inherits project. */
  shotVideoModelKey?: string | null
  /** Current shot resolution override; cleared locally when invalid for the new model. */
  shotVideoResolution?: string | null
  onSaved?: (next: ShotVideoModelSaved) => void
  /** chip — подпись в шапке шота; param — параметр пульта генерации. */
  variant?: "chip" | "param"
  placement?: "above" | "below"
}

/** Модель видео шота: меню прямо у подписи; своя модель подсвечена. */
export function ShotVideoModelChip({
  projectId,
  shotId,
  shotVideoModelKey,
  shotVideoResolution,
  onSaved,
  variant = "chip",
  placement = "below",
}: ShotVideoModelChipProps) {
  const { t } = useTranslation(["shot", "common", "workspace"])
  const { project, project_permissions, generation_models_by_context } = useShotPanelWorkspace()
  const canEdit = Boolean(project_permissions?.can_edit_content)
  const models = useMemo(
    () => generation_models_by_context?.shot_video ?? [],
    [generation_models_by_context?.shot_video],
  )
  const projectKey = project?.effective_video_model?.toString().trim() || ""
  const projectLabel = findShotVideoModel(projectKey, models)?.label?.trim() || projectKey || "—"
  const rawOverride = shotVideoModelKey?.toString().trim() || null
  // Своя модель, совпадающая с проектной, — то же, что модель проекта.
  const override = rawOverride && rawOverride !== projectKey ? rawOverride : null
  const currentKey = effectiveShotVideoModelKey(shotVideoModelKey, project)
  const label = findShotVideoModel(currentKey, models)?.label?.trim() || currentKey || "—"

  const options = useMemo(() => sortedCatalogModelOptions(models, t("common:platformDisabledModel")), [models, t])

  const handleChange = (picked: string | null) => {
    const nextOverride = picked && picked !== projectKey ? picked : null
    const prevOverride = rawOverride
    const prevResolution = shotVideoResolution?.toString().trim() || null
    // Same as server: blank shot model → validate against project effective model.
    const nextResolution = videoResolutionAfterModelChange(shotVideoResolution, nextOverride ?? projectKey, models)
    onSaved?.({ videoModelKey: nextOverride, videoResolution: nextResolution })
    void patchShotVideoModelKey(projectId, shotId, nextOverride).catch((err: Error) => {
      onSaved?.({ videoModelKey: prevOverride, videoResolution: prevResolution })
      toast.error(err.message || t("common.saveErrorRetry"))
    })
  }

  return (
    <ShotModelMenu
      value={override}
      options={options}
      projectLabel={projectLabel}
      label={label}
      hint={t("panel.changeVideoModel")}
      canEdit={canEdit}
      variant={variant}
      placement={placement}
      onChange={handleChange}
    />
  )
}
